"use client";

import { tokens } from "@bandwith/styles";
import { useColorScheme } from "@bandwith/ui";
import { memo, type ReactNode, useEffect, useRef, useState } from "react";
import { type Finger, SVGuitarChord } from "svguitar";
import { type ChordDefinition, playChordFromDefinition } from "./chordAudio";

/**
 * Simple deep equality check for plain objects and arrays.
 * Handles primitives, arrays, and plain objects (no functions, dates, etc.)
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== typeof b) return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  if (typeof a === "object" && typeof b === "object") {
    const aObj = a as Record<string, unknown>;
    const bObj = b as Record<string, unknown>;
    const aKeys = Object.keys(aObj);
    const bKeys = Object.keys(bObj);
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
      if (!deepEqual(aObj[key], bObj[key])) return false;
    }
    return true;
  }

  return false;
}

/**
 * Custom props comparison for SVGuitarRenderer memo.
 * Does deep comparison of chord and fingering, shallow for other props.
 */
function areSVGuitarPropsEqual(
  prev: SVGuitarRendererProps,
  next: SVGuitarRendererProps,
): boolean {
  // Deep compare chord and fingering (these are plain data objects)
  if (!deepEqual(prev.chord, next.chord)) return false;
  if (!deepEqual(prev.fingering, next.fingering)) return false;
  // Shallow compare other props
  if (prev.frets !== next.frets) return false;
  if (prev.width !== next.width) return false;
  if (prev.className !== next.className) return false;
  if (prev.onClick !== next.onClick) return false;
  if (prev.children !== next.children) return false;
  if (prev.showFretMarkers !== next.showFretMarkers) return false;
  return true;
}

// svguitar writes colors into SVG attributes, which can't hold CSS
// variables, so the diagram takes literal values for the resolved theme.
const PALETTES = { dark: tokens.colors, light: tokens.lightColors } as const;
/**
 * Fret markers: the grid line color at 40% opacity, as an 8-digit hex.
 * Assumes 6-digit hex tokens (checked by spa/src/test/lib/theme-parity.test.ts).
 */
const FRET_MARKER_ALPHA = "66";
const BACKGROUND_COLOR = "transparent";
const FONT_FAMILY = "JetBrains Mono Variable, monospace";

export interface SVGuitarDimensions {
  width: number;
  height: number;
  nutY: number;
  fretSpacing: number;
  stringSpacing: number;
  leftPadding: number;
}

type Barre = { fromString: number; toString: number; fret: number };

export interface SVGuitarRendererProps {
  /** Chord definition to render */
  chord: ChordDefinition;
  /** Optional fingering override (displays finger numbers on dots) */
  fingering?: (number | "-")[];
  /** Number of frets to display */
  frets?: number;
  /** Width of the diagram in pixels */
  width?: number;
  /** Additional CSS classes */
  className?: string;
  /** Click handler - when provided, renders as a button */
  onClick?: () => void;
  /** Render prop for overlay content (receives calculated dimensions) */
  children?: (dimensions: SVGuitarDimensions) => ReactNode;
  /** Show fret position markers (dots at 3, 5, 7, 9, double at 12) */
  showFretMarkers?: boolean;
}

/**
 * Convert ChordDefinition to SVGuitar finger format.
 *
 * ChordPro: frets array is low-to-high [lowE, A, D, G, B, highE] (indices 0-5)
 * SVGuitar: string numbers are 6 (low E) to 1 (high E)
 */
function convertToFingers(
  chord: ChordDefinition,
  fingering?: (number | "-")[],
): Finger[] {
  const fingers: Finger[] = [];
  const numStrings = chord.frets.length;

  for (let i = 0; i < numStrings; i++) {
    const fret = chord.frets[i];
    if (fret === undefined) continue;

    const stringNum = numStrings - i;
    const finger = fingering ? fingering[i] : chord.fingers?.[i];
    const fingerText =
      finger !== undefined && finger !== "-" ? String(finger) : undefined;

    if (fret === "x" || fret === -1) {
      fingers.push([stringNum, "x"]);
    } else if (fret === 0) {
      fingers.push([stringNum, 0]);
    } else if (fingerText) {
      fingers.push([stringNum, fret, fingerText]);
    } else {
      fingers.push([stringNum, fret]);
    }
  }

  return fingers;
}

/**
 * Build barre specifications from chord data.
 *
 * Uses barres data from the chord database when available (authoritative source).
 * Falls back to detecting barres from finger patterns for user-defined chords.
 */
function getBarres(chord: ChordDefinition): Barre[] {
  const numStrings = chord.frets.length;

  // If we have authoritative barres data from the chord database, use it
  if (chord.barres && chord.barres.length > 0) {
    return chord.barres.map((barreFret) => {
      // Find all strings at this fret to determine the barre range
      const stringsAtFret: number[] = [];
      for (let i = 0; i < numStrings; i++) {
        const fret = chord.frets[i];
        if (fret === barreFret) {
          // Convert index to SVGuitar string number (0 -> 6, 5 -> 1)
          stringsAtFret.push(numStrings - i);
        }
      }

      // Barre spans from highest to lowest string at this fret
      const fromString = Math.max(...stringsAtFret);
      const toStringNum = Math.min(...stringsAtFret);

      return { fromString, toString: toStringNum, fret: barreFret };
    });
  }

  // Fall back to detecting barres from finger patterns (for user-defined chords)
  if (!chord.fingers) return [];

  // Group strings by (finger, fret) - potential barres
  const fingerFretGroups = new Map<string, number[]>();

  for (let i = 0; i < numStrings; i++) {
    const fret = chord.frets[i];
    const finger = chord.fingers[i];

    // Only consider fretted notes (not open or muted) with a defined finger
    if (
      typeof fret !== "number" ||
      fret <= 0 ||
      typeof finger !== "number" ||
      finger <= 0
    ) {
      continue;
    }

    const key = `${finger}-${fret}`;
    const existing = fingerFretGroups.get(key) ?? [];
    existing.push(i);
    fingerFretGroups.set(key, existing);
  }

  const barres: Barre[] = [];

  for (const [key, stringIndices] of fingerFretGroups) {
    // A barre requires at least 2 strings
    if (stringIndices.length < 2) continue;

    const fret = Number.parseInt(key.split("-")[1] ?? "0", 10);
    // Convert indices to SVGuitar string numbers (0 -> 6, 5 -> 1)
    const svguitarStrings = stringIndices.map((i) => numStrings - i);

    // Barre spans from highest string number to lowest
    const fromString = Math.max(...svguitarStrings);
    const toStringNum = Math.min(...svguitarStrings);

    barres.push({ fromString, toString: toStringNum, fret });
  }

  return barres;
}

/** Standard guitar fret marker positions (0-indexed for svguitar) */
const FRET_MARKERS: (number | { fret: number; double: true })[] = [
  2,
  4,
  6,
  8, // Frets 3, 5, 7, 9
  { fret: 11, double: true }, // Fret 12
  14,
  16,
  18,
  20, // Frets 15, 17, 19, 21
  { fret: 23, double: true }, // Fret 24
];

/** Filter fret markers to those within the displayed range */
function buildFretMarkers(
  numFrets: number,
): (number | { fret: number; double: true })[] {
  return FRET_MARKERS.filter((m) => {
    const fret = typeof m === "number" ? m : m.fret;
    return fret < numFrets;
  });
}

export const SVGuitarRenderer = memo(function SVGuitarRenderer({
  chord,
  fingering,
  frets = 4,
  width = 100,
  className = "",
  onClick,
  children,
  showFretMarkers = false,
}: SVGuitarRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = useColorScheme();
  const [dimensions, setDimensions] = useState<SVGuitarDimensions | null>(null);

  const strings = chord.frets.length;
  const title = chord.name;
  const position = chord.baseFret > 1 ? chord.baseFret : undefined;

  useEffect(() => {
    if (!containerRef.current) return;

    // Clear previous content
    containerRef.current.innerHTML = "";

    const fingers = convertToFingers(chord, fingering);
    const barres = getBarres(chord);
    const palette = PALETTES[theme];

    try {
      new SVGuitarChord(containerRef.current)
        .configure({
          strings,
          frets,
          position,
          title,
          color: palette.amber,
          backgroundColor: BACKGROUND_COLOR,
          fingerColor: palette.amber,
          fingerTextColor: palette.onAccent,
          titleColor: palette.amber,
          stringColor: palette.gridLine,
          fretColor: palette.gridLine,
          nutWidth: 8,
          fixedDiagramPosition: true,
          fontFamily: FONT_FAMILY,
          titleFontSize: 28,
          fingerSize: frets > 4 ? 0.55 : 0.6,
          strokeWidth: 2,
          fretSize: frets > 4 ? 1.2 : undefined,
          sidePadding: frets > 4 ? 0.15 : undefined,
          topFretWidth: frets > 4 ? 8 : undefined,
          // Fret markers (native svguitar feature)
          showFretMarkers,
          fretMarkers: showFretMarkers ? buildFretMarkers(frets) : undefined,
          fretMarkerColor: `${palette.gridLine}${FRET_MARKER_ALPHA}`,
          fretMarkerSize: 0.3,
          doubleFretMarkerDistance: 0.6,
        })
        .chord({
          fingers,
          barres,
        })
        .draw();

      // Resize the SVG and calculate dimensions
      const svg = containerRef.current.querySelector("svg");
      if (svg) {
        svg.setAttribute("width", String(width));
        svg.removeAttribute("height");
        svg.style.maxWidth = "100%";

        // Calculate dimensions for overlay positioning
        const viewBox = svg.getAttribute("viewBox")?.split(" ").map(Number);
        if (viewBox && viewBox.length === 4) {
          const vbWidth = viewBox[2] ?? 1;
          const vbHeight = viewBox[3] ?? 0;
          const scale = width / vbWidth;

          const topPadding = vbHeight * 0.08 * scale;
          const diagramHeight = vbHeight * 0.92 * scale;
          const sidePadding = vbWidth * 0.15 * scale;

          setDimensions({
            width,
            height: vbHeight * scale,
            nutY: topPadding + 8,
            fretSpacing: diagramHeight / (frets + 1),
            stringSpacing: (width - sidePadding * 2) / (strings - 1),
            leftPadding: sidePadding,
          });
        }
      }
    } catch (e) {
      console.error("Failed to render chord diagram:", e);
    }
  }, [
    chord,
    fingering,
    frets,
    theme,
    strings,
    title,
    position,
    width,
    showFretMarkers,
  ]);

  // Default to playable when not in interactive mode (no children)
  const handleClick = !children
    ? (onClick ?? (() => playChordFromDefinition(chord)))
    : onClick;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Conditionally interactive - role/tabIndex are set when handleClick exists
    <div
      className={`relative ${handleClick ? "cursor-pointer" : ""} ${className}`}
      onClick={handleClick}
      onKeyDown={
        handleClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
      role={handleClick ? "button" : undefined}
      tabIndex={handleClick ? 0 : undefined}
      title={handleClick && chord.name ? `Play ${chord.name}` : undefined}
    >
      <div ref={containerRef} />
      {children && dimensions && children(dimensions)}
    </div>
  );
}, areSVGuitarPropsEqual);
